import { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { APP_LINKS } from '@/helpers/links';
import { ROLES } from '@/helpers/constants';
import { InfoMessage } from '@/components/InfoMessage';
import { useApplicationStatusByFundraisingId } from '@/hooks/shared/useApplicationStatusByFundraisingId';
import { useGetFundraisingStatus } from '@/query/queries/fundraising/useGetFundraisingStatus';
import { FR_APPLICATION_STATUSES, FR_STEPS } from '@/helpers/fundraising';
import IconCheck from './icon_check.svg';
import IconClose from './icon_close.svg';
import s from './index.module.scss';

export const ApplicationMessages = ({ role, fundraisingId, className }) => {
  const { t } = useTranslation('fundraising');
  const { application } = useApplicationStatusByFundraisingId(fundraisingId);
  const { data: status } = useGetFundraisingStatus();

  const [close, setClose] = useState(false);

  if (!application || !status) return null;
  if (role === ROLES.founder && status.step === FR_STEPS.fundraising_summary) return null;
  if (
    role === ROLES.co_founder &&
    status.step === FR_STEPS.fundraising_summary &&
    status.is_confirmed
  )
    return null;

  let message = null;

  if (application.status === FR_APPLICATION_STATUSES.APPROVED && !close) {
    message = (
      <div className={s.success}>
        <img src={IconCheck} alt="check" width={24} height={24} />
        <div className={s.success_content}>
          <div className={s.success_title}>{t('app_messages.fundraising_goal_ready')}</div>
          <div className={s.success_desc}>{t('app_messages.calculated_funding_goal')}</div>
        </div>
        <button type="button" onClick={() => setClose(true)}>
          <img src={IconClose} alt="close" width={24} height={24} />
        </button>
      </div>
    );
  }

  if (
    (application.status === null &&
      !Object.keys(application.reporting.required_documents).length) ||
    application.status === FR_APPLICATION_STATUSES.IN_REVIEW ||
    application.status === FR_APPLICATION_STATUSES.PRE_CHECKS ||
    application.status === FR_APPLICATION_STATUSES.RESUBMITTED
  ) {
    message = (
      <InfoMessage
        type="info"
        title={t('app_messages.application_under_review')}
        description={t('app_messages.received_financial_documents')}
      />
    );
  }

  if (application.status === FR_APPLICATION_STATUSES.CHANGES_REQUESTED) {
    message = (
      <InfoMessage
        type="action"
        title={t('app_messages.action_required')}
        description={application.comment}
        linkLabel={t('app_messages.update_documents')}
        linkUrl={APP_LINKS.fundraisingApplication}
        linkState={{
          fundraisingId,
          newFunds: application.new_funds,
          refinancing:
            application.refinancings?.filter(fundraising => fundraising.checked) ?? [],
        }}
      />
    );
  }

  if (application.status === FR_APPLICATION_STATUSES.REJECTED) {
    message = (
      <InfoMessage
        type="error"
        title={t('app_messages.application_rejected')}
        description={application.comment}
      />
    );
  }

  if (!message) return null;

  return <div className={classnames(s.wrapper, className)}>{message}</div>;
};
